/**
 * API keys feature — Zod schemas.
 *
 * Platform-scoped admin API keys. These schemas are the wire
 * contract with the backend's `backend/pkg/api/admin/apikey.go`;
 * every field here must match a JSON tag on either
 * APIKeyResponse (reads), CreateAPIKeyRequest (writes), or
 * CreateAPIKeyResponse (one-time reveal).
 *
 * The backend model is role-based: every API key inherits a
 * single RBAC role from the creating admin, optionally
 * downgraded at creation via target_role. It is NOT
 * scope-based. An earlier revision of this file modelled
 * scopes (admin.read / platform.write / etc.) but the backend
 * never shipped that surface — the `/api-keys` page failed
 * every Zod parse until the shape was aligned. Keep the
 * frontend and backend on the role model in lockstep.
 */

import { z } from "zod";

// Role names match rbac.Role on the backend. If a new role is
// added to rbac.go the backend's CHECK will refuse writes
// until this enum is widened too.
export const apiKeyRoleSchema = z.enum([
  "platform_admin",
  "platform_operator",
  "tenant_admin",
  "tenant_operator",
  "tenant_readonly",
]);
export type ApiKeyRole = z.infer<typeof apiKeyRoleSchema>;

export const apiKeyStatusSchema = z.enum(["active", "revoked"]);
export type ApiKeyStatus = z.infer<typeof apiKeyStatusSchema>;

// APIKeyResponse → ApiKey. `token_prefix` is the 10-char
// display fragment ("pureid_ak_a"). `tenant_id` is present
// only for tenant-scoped keys; platform keys omit it. Times
// that may be unset (last_used, expires, revoked) are
// `omitempty` on the backend so they arrive as `undefined`
// — optional, NOT nullable. The backend never emits null
// for these.
export const apiKeySchema = z.object({
  id: z.string(),
  admin_user_id: z.string(),
  tenant_id: z.string().optional(),
  name: z.string(),
  token_prefix: z.string(),
  role: apiKeyRoleSchema,
  status: apiKeyStatusSchema,
  last_used_at: z.string().optional(),
  expires_at: z.string().optional(),
  created_at: z.string(),
  revoked_at: z.string().optional(),
});
export type ApiKey = z.infer<typeof apiKeySchema>;

// ListAPIKeysResponse wraps items under `items`. No cursor —
// the /me list fits comfortably in one page.
export const apiKeysListResponseSchema = z.object({
  items: z.array(apiKeySchema),
});
export type ApiKeysListResponse = z.infer<typeof apiKeysListResponseSchema>;

// CreateAPIKeyRequest body. target_role is an optional
// downgrade from the caller's role (same-or-lower rank only,
// enforced by the service's rank rule on the backend).
// Omitted → backend inherits the caller's role unchanged.
// TTL uses seconds on the wire but this form captures days
// for UX; the mutation converts on the way out.
export const createApiKeySchema = z.object({
  name: z.string().min(1, "Name is required").max(80),
  target_role: apiKeyRoleSchema.optional(),
  expires_in_days: z
    .number()
    .int()
    .positive()
    .max(3650)
    .optional(),
});
export type CreateApiKeyInput = z.infer<typeof createApiKeySchema>;

// CreateAPIKeyResponse — the one-time reveal. The key row
// lives under `api_key`; `raw_token` is the plaintext shown
// exactly once. Both this file and the dialog treat
// raw_token as a write-only artefact that MUST NOT land in
// any query cache.
export const createApiKeyResponseSchema = z.object({
  api_key: apiKeySchema,
  raw_token: z.string(),
});
export type CreateApiKeyResponse = z.infer<typeof createApiKeyResponseSchema>;
